import { useState, useEffect, useCallback, useMemo } from 'react';
import type { TreeProps } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { getAncestors } from 'components/common/popover-tree-selector/tree-utils';

type UsePopoverTreeExpansionParams = {
  isPopoverOpen: boolean;
  searchValue: string;
  selectedKey?: React.Key;
  searchMatchKeys: React.Key[];
  parentMap: Record<string, React.Key | null>;
};

type UsePopoverTreeExpansionResult = {
  expandedKeys: React.Key[];
  handleExpand: TreeProps['onExpand'];
};

export const usePopoverTreeExpansion = ({
  isPopoverOpen,
  searchValue,
  selectedKey,
  searchMatchKeys,
  parentMap
}: UsePopoverTreeExpansionParams): UsePopoverTreeExpansionResult => {
  const [manuallyExpandedKeys, setManuallyExpandedKeys] = useState<React.Key[] | null>(null);

  const calculatedExpandedKeys = useMemo(() => {
    if (!isPopoverOpen) {
      return [];
    }

    if (!isEmpty(searchMatchKeys)) {
      const searchMatchAncestors = getAncestors(searchMatchKeys, parentMap);
      return Array.from(new Set<React.Key>([...searchMatchKeys, ...searchMatchAncestors]));
    }

    return selectedKey ? getAncestors(selectedKey, parentMap) : [];
  }, [isPopoverOpen, searchMatchKeys, selectedKey, parentMap]);

  useEffect(() => {
    setManuallyExpandedKeys(null);
  }, [isPopoverOpen, searchValue]);

  const handleExpand: TreeProps['onExpand'] = useCallback((keys: React.Key[]) => {
    setManuallyExpandedKeys(keys);
  }, []);

  return {
    expandedKeys: manuallyExpandedKeys ?? calculatedExpandedKeys,
    handleExpand
  };
};
